import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';

const lbl = { fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 };
const inp = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', outline: 'none', background: 'white', boxSizing: 'border-box' };
const EMPTY = { name: '', description: '', bulknet_price: '', retail_price: '', speed_mbps: '', landlord_id: '' };
const fmt = (n) => parseFloat(n || 0).toFixed(2);

export default function ServicePlans() {
  const [plans, setPlans] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filterLandlord, setFilterLandlord] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/employee/service-plans'),
      api.get('/employee/landlords'),
    ]).then(([plansRes, llRes]) => {
      setPlans(plansRes.data.plans || []);
      setLandlords(llRes.data.landlords || []);
    }).catch(() => toast.error('Could not load data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleEdit = (p) => {
    setForm({ name: p.name, description: p.description || '', bulknet_price: p.bulknet_price || '', retail_price: p.retail_price || '', speed_mbps: p.speed_mbps || '', landlord_id: p.landlord_id || '' });
    setEditingId(p.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    if (!form.name || !form.bulknet_price || !form.landlord_id) { toast.error('Landlord, name, and price are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, bulknet_price: parseFloat(form.bulknet_price), retail_price: form.retail_price ? parseFloat(form.retail_price) : null, speed_mbps: form.speed_mbps ? parseInt(form.speed_mbps) : null };
      if (editingId) {
        await api.patch('/employee/service-plans/' + editingId, payload);
        toast.success('Plan updated!');
      } else {
        await api.post('/employee/service-plans', payload);
        toast.success('Plan created!');
      }
      setShowForm(false); setEditingId(null); setForm(EMPTY); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save plan'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch('/employee/service-plans/' + p.id, { is_active: !p.is_active });
      toast.success(p.is_active ? 'Plan deactivated' : 'Plan activated');
      load();
    } catch { toast.error('Could not update plan'); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    try { await api.delete('/employee/service-plans/' + id); toast.success('Plan deleted'); load(); }
    catch { toast.error('Could not delete plan'); }
  };

  const filteredPlans = filterLandlord ? plans.filter(p => p.landlord_id === filterLandlord) : plans;
  const grouped = filteredPlans.reduce((acc, p) => {
    const key = p.landlord_id || 'unassigned';
    if (!acc[key]) acc[key] = { name: p.landlord_name || 'Unassigned', plans: [] };
    acc[key].plans.push(p);
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <EmployeeSidebar />
      <div style={{ flex: 1, padding: '2rem', maxWidth: 1000 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div>
            <h1 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: 4 }}>Service Plans</h1>
            <p style={{ color: '#64748b', fontSize: 14 }}>Custom internet service plans per landlord.</p>
          </div>
          <button onClick={() => { setForm(EMPTY); setEditingId(null); setShowForm(true); }} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            + New Plan
          </button>
        </div>

        {showForm && (
          <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: '1.5rem', marginTop: 16, marginBottom: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>{editingId ? 'Edit Plan' : 'New Plan'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Landlord *</label>
                <select value={form.landlord_id} onChange={e => setForm(p => ({ ...p, landlord_id: e.target.value }))} style={inp}>
                  <option value="">Select landlord...</option>
                  {landlords.map(l => <option key={l.id} value={l.id}>{l.first_name} {l.last_name}{l.company_name ? ' — ' + l.company_name : ''}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Plan Name *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inp} placeholder="e.g. Basic 500 Mbps" />
              </div>
              <div>
                <label style={lbl}>Our Price / unit / mo ($) *</label>
                <input type="number" step="0.01" value={form.bulknet_price} onChange={e => setForm(p => ({ ...p, bulknet_price: e.target.value }))} style={inp} placeholder="45.00" />
              </div>
              <div>
                <label style={lbl}>Retail Price / unit / mo ($)</label>
                <input type="number" step="0.01" value={form.retail_price} onChange={e => setForm(p => ({ ...p, retail_price: e.target.value }))} style={inp} placeholder="89.99" />
              </div>
              <div>
                <label style={lbl}>Speed (Mbps)</label>
                <input type="number" value={form.speed_mbps} onChange={e => setForm(p => ({ ...p, speed_mbps: e.target.value }))} style={inp} placeholder="500" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} style={{ ...inp, minHeight: 60, resize: 'vertical' }} placeholder="What is included..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY); }} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '9px 20px', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : editingId ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, margin: '16px 0 20px' }}>
          <select value={filterLandlord} onChange={e => setFilterLandlord(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, background: 'white', minWidth: 220 }}>
            <option value="">All Landlords</option>
            {landlords.map(l => <option key={l.id} value={l.id}>{l.first_name} {l.last_name}</option>)}
          </select>
          <div style={{ marginLeft: 'auto', padding: '8px 0', fontSize: 13, color: '#64748b' }}>{filteredPlans.length} plan{filteredPlans.length !== 1 ? 's' : ''}</div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</div>
        ) : Object.keys(grouped).length === 0 ? (
          <div style={{ background: 'white', borderRadius: 12, padding: '3rem', textAlign: 'center', color: '#94a3b8', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📋</div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>No service plans yet</div>
            <div style={{ fontSize: 13 }}>Click New Plan to create a custom plan for a landlord.</div>
          </div>
        ) : (
          Object.entries(grouped).map(([landlordId, group]) => (
            <div key={landlordId} style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', marginBottom: 20, overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{group.name}</span>
                <span style={{ background: '#e2e8f0', color: '#64748b', borderRadius: 100, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{group.plans.length} plan{group.plans.length !== 1 ? 's' : ''}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    {['Plan Name', 'Speed', 'Our Price', 'Retail Price', 'Description', 'Active', ''].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.plans.map((p, i) => (
                    <tr key={p.id} style={{ borderTop: '1px solid #f1f5f9', opacity: p.is_active === false ? 0.5 : 1 }}>
                      <td style={{ padding: '12px 16px', fontWeight: 700 }}>{p.name}</td>
                      <td style={{ padding: '12px 16px', color: '#2563eb', fontWeight: 600 }}>{p.speed_mbps ? p.speed_mbps + ' Mbps' : '—'}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: '#16a34a' }}>${fmt(p.bulknet_price)}/mo</td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }}>{p.retail_price ? '$' + fmt(p.retail_price) + '/mo' : '—'}</td>
                      <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 12 }}>{p.description || '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <button onClick={() => toggleActive(p)} style={{ background: p.is_active !== false ? '#dcfce7' : '#f1f5f9', color: p.is_active !== false ? '#16a34a' : '#94a3b8', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                          {p.is_active !== false ? 'On' : 'Off'}
                        </button>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleEdit(p)} style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
                          <button onClick={() => deletePlan(p.id)} style={{ background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
        <LegalFooter />
      </div>
    </div>
  );
}
